import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import axios from 'axios';
import { toast } from 'react-toastify';
import listPlugin from '@fullcalendar/list';
import frLocale from '@fullcalendar/core/locales/fr';
import LoadingBar from 'react-top-loading-bar';
import AppointmentPopover from './AppointmentPopover';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthProvider';
import { API_BASE_URL } from '../../api';

import './Calendar.scss';

// Remembers which view (Mois/Semaine/Agenda) the user last picked, so the
// calendar reopens on it instead of always defaulting to "Semaine".
const CALENDAR_VIEW_BOOKMARK_KEY = 'calendar:defaultView';

const AppointmentsCalendar = () => {
  const { t } = useTranslation();
  const { hasRight } = useAuth();
  const canCreate = hasRight('CALENDAR_CREATE');
  const canEdit = hasRight('CALENDAR_EDIT');
  const [events, setEvents] = useState([]);
  const [defaultValues, setDefaultValues] = useState(null);
  const calendarRef = useRef(null);
  const loaderRef = useRef(null);
  const [showPopover, setShowPopover] = useState(false);
  const [initialView, setInitialView] = useState('timeGridWeek');
  const [viewReady, setViewReady] = useState(false);
  const lastSavedView = useRef('timeGridWeek');
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    axios.get(`${API_BASE_URL}/api/v1/ui-bookmarks?key=${encodeURIComponent(CALENDAR_VIEW_BOOKMARK_KEY)}`, { withCredentials: true })
      .then(res => {
        if (res.status === 200 && res.data) {
          // A bare string value can't be told apart from "already parsed by
          // axios" via typeof (unlike an object payload) — try to unwrap it,
          // but silently keep the raw value when it's already unwrapped
          // rather than letting JSON.parse throw and swallowing the update.
          let saved = res.data;
          if (typeof saved === 'string') {
            try { saved = JSON.parse(saved); } catch { /* already unwrapped */ }
          }
          if (saved) {
            setInitialView(saved);
            lastSavedView.current = saved;
          }
        }
      })
      .catch(() => {})
      .finally(() => setViewReady(true));
  }, []);

  const handleDatesSet = (info) => {
    if (info.view.type === lastSavedView.current) return;
    lastSavedView.current = info.view.type;
    axios.put(
      `${API_BASE_URL}/api/v1/ui-bookmarks?key=${encodeURIComponent(CALENDAR_VIEW_BOOKMARK_KEY)}`,
      JSON.stringify(info.view.type),
      { withCredentials: true, headers: { 'Content-Type': 'application/json' } }
    ).catch(() => {});
  };

  const hidePopover = () => {
    calendarRef.current?.getApi().unselect();
    setShowPopover(false);
  };

  const TYPE_COLORS = {
    SERVICE: '#2563eb',
    TASK:    '#d97706',
    EVENT:   '#7c3aed',
  };

  const fetchAppointments = () => {
    loaderRef.current?.continuousStart();
    axios.get(`${API_BASE_URL}/api/v1/appointments`, { withCredentials: true })
      .then(response => {
        const fetchedEvents = response.data.map(appointment => {
          const type = appointment.appointmentType || 'SERVICE';
          const title = type === 'SERVICE'
            ? `${appointment.medicalService?.name ?? 'Service'}\n${appointment.patient?.fullName ?? ''}`
            : (appointment.title || '');
          return {
            id: String(appointment.id),
            title,
            start: appointment.startTime,
            end: appointment.endTime,
            extendedProps: { type },
          };
        });
        setEvents(fetchedEvents);
        loaderRef.current?.complete();
      })
      .catch(error => {
        console.error("Erreur de chargement des rendez-vous :", error);
        loaderRef.current?.complete();
      });
  };

  useEffect(() => { fetchAppointments(); }, []);

  // Partagé par le clic sur un événement du calendrier et par l'ouverture directe
  // d'un rendez-vous depuis une notification (?appointmentId=... dans l'URL) — dans ce
  // second cas il n'y a pas de clickInfo FullCalendar, donc start/end retombent sur les
  // horaires renvoyés par l'API.
  const openAppointmentById = (id, { start, end } = {}) => {
    axios.get(`${API_BASE_URL}/api/v1/appointement/${id}`, { withCredentials: true })
      .then(response => {
        const fromDate = start ?? response.data.startTime;
        const toDate = end ?? response.data.endTime ?? fromDate;
        setDefaultValues({
          id,
          fromDate,
          toDate,
          patientId: response.data.patient?.id,
          medicalServiceId: response.data.medicalService?.id,
          description: response.data.description,
          title: response.data.title,
          appointmentType: response.data.appointmentType || 'SERVICE',
        });
        setShowPopover(true);
      })
      .catch(err => console.error("Erreur fetch by ID :", err));
  };

  const handleEventClick = (clickInfo) => {
    const start = clickInfo.event.start;
    const end = clickInfo.event.end;
    openAppointmentById(clickInfo.event.id, {
      start: start?.toISOString(),
      end: end instanceof Date ? end.toISOString() : end,
    });
  };

  // Arrivée depuis une notification de rappel de rendez-vous — ouvre directement sa
  // fiche, puis retire le paramètre pour ne pas la rouvrir si l'utilisateur revient sur
  // cette page plus tard (bouton retour, etc.).
  useEffect(() => {
    const appointmentId = searchParams.get('appointmentId');
    if (!appointmentId) return;
    openAppointmentById(appointmentId);
    searchParams.delete('appointmentId');
    setSearchParams(searchParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleSelect = (info) => {
    setDefaultValues({
      fromDate: info.startStr,
      toDate: info.endStr,
    });
    setShowPopover(true);
  };

  const handleAddNew = () => {
    setDefaultValues({});
    setShowPopover(true);
  };

  const handleEventChange = (changeInfo) => {
    const updatedEvent = {
      id: changeInfo.event.id,
      fromDate: changeInfo.event.start.toISOString(),
      toDate: changeInfo.event.end.toISOString(),
    };
    axios.put(`${API_BASE_URL}/api/v1/updateAppointment/${updatedEvent.id}`, updatedEvent, { withCredentials: true })
      .then(() => toast.success('Appointment updated'))
      .catch(err => console.error("Erreur mise à jour :", err));
  };

  const renderEventContent = (eventInfo) => {
    const type = eventInfo.event.extendedProps?.type || 'SERVICE';
    const fmt = (d) => d?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    if (type === 'SERVICE') {
      const [service, patient] = eventInfo.event.title.split('\n');
      return (
        <div className="cal-event">
          <span className="cal-event__time">{fmt(eventInfo.event.start)}</span>
          <span className="cal-event__service">{service}</span>
          {patient && <span className="cal-event__patient">{patient}</span>}
        </div>
      );
    }
    return (
      <div className="cal-event">
        <span className="cal-event__time">{fmt(eventInfo.event.start)}</span>
        <span className="cal-event__service">{eventInfo.event.title}</span>
      </div>
    );
  };

  const renderDayHeader = (arg) => {
    const viewType = arg.view?.type || '';
    const isTimeGrid = viewType.includes('timeGrid');
    const weekday = arg.date.toLocaleDateString('fr-FR', { weekday: 'short' })
      .replace('.', '').toUpperCase();
    const num = arg.date.getDate();

    if (isTimeGrid) {
      return (
        <div className={`cal-day-header${arg.isToday ? ' cal-day-header--today' : ''}`}>
          <span className="cal-day-header__wd">{weekday}</span>
          <span className="cal-day-header__num">{num}</span>
        </div>
      );
    }
    return <span className="cal-day-header__wd">{weekday}</span>;
  };

  const customButtons = canCreate
    ? { addNew: { text: `+ ${t('New Appointment')}`, click: handleAddNew } }
    : {};

  const headerToolbar = {
    left: 'today prev,next title',
    right: canCreate
      ? 'dayGridMonth,timeGridWeek,listWeek addNew'
      : 'dayGridMonth,timeGridWeek,listWeek',
  };

  return (
    <>
      <LoadingBar color="#2563eb" ref={loaderRef} height={2} shadow={false} />

      <div className="calendar-container">
        {viewReady && (
        <FullCalendar
          ref={calendarRef}
          locale={frLocale}
          firstDay={1}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
          initialView={initialView}
          editable={canEdit}
          selectable={canCreate}
          selectMirror={true}
          select={handleSelect}
          events={events}
          eventClick={handleEventClick}
          eventDrop={handleEventChange}
          eventResize={handleEventChange}
          eventContent={renderEventContent}
          dayHeaderContent={renderDayHeader}
          datesSet={handleDatesSet}
          allDaySlot={false}
          weekends={true}
          nowIndicator={true}
          height="100%"
          slotMinTime="00:00:00"
          slotMaxTime="24:00:00"
          scrollTime="08:00:00"
          slotDuration="00:30:00"
          slotLabelInterval="01:00:00"
          dayHeaderFormat={{ weekday: 'short', day: 'numeric' }}
          unselectAuto={false}
          customButtons={customButtons}
          headerToolbar={headerToolbar}
          eventClassNames={(arg) => {
            const now = new Date();
            return arg.event.end && arg.event.end < now ? ['fc-event-past'] : [];
          }}
          eventDidMount={(info) => {
            const now = new Date();
            const isPast = info.event.end && info.event.end < now;
            if (isPast) return;
            const type = info.event.extendedProps?.type || 'SERVICE';
            const color = TYPE_COLORS[type] || TYPE_COLORS.SERVICE;
            info.el.style.setProperty('background-color', color, 'important');
            info.el.style.setProperty('border-color', color, 'important');
          }}
          buttonText={{
            today: "Aujourd'hui",
            month: 'Mois',
            week: 'Semaine',
            day: 'Jour',
            list: 'Agenda',
          }}
        />
        )}
      </div>

      <AppointmentPopover
        show={showPopover}
        defaultValues={defaultValues}
        onHide={hidePopover}
        onSave={() => { fetchAppointments(); setShowPopover(false); }}
      />
    </>
  );
};

export default AppointmentsCalendar;
